// dependencies
import { useQueries } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";

// hooks
import { useNPSSummaryRange } from "../../../../api/hooks";

import { ICohortModel, getNPSSummaryRange } from "../../../../api/services";

// utils
import { getColor } from "../../../../utils/getColor";
import { Hex, chooseBestContrastingColor } from "../../../../utils/getContrast";
// import { generateDateRangeLabels, getPointsRadiusArray } from "./utils";
import toFixed from "../../../../utils/toFixed";
import { generateDateRangeLabels, getPointsRadiusArray } from "../SatisfactionTrendlineChart/utils";

// config
import Config from "../../Config";
import { IEngagementFilter, ISummaryRequestParams } from "../../schema";
import { ILegendItem } from "../CohortSelect";

// constants
const LINE_OPACITY_HEX = "4F"; // 4F adds 31% opacity to background color

// hook for retrieving formatted data for overall satisfaction line chart
const useNPSTrendlineChart = (
	filter: IEngagementFilter,
	month: Date | null,
	cohort: string,
	selectedCohorts: ICohortModel[] = []
) => {
	// use react query hook to fetch overall data
	const { data: eNPSSummaryData, isLoading } = useNPSSummaryRange({
		startDate: filter.startDate,
		endDate: filter.endDate,
		binSize: Config.BIN_SIZE,
	});

	const cohortsResponses = useQueries({
		queries: [
			...selectedCohorts.map((cohort) => {
				const params = {
					startDate: filter.startDate,
					endDate: filter.endDate,
					binSize: Config.BIN_SIZE,
					cohortIds: cohort._id,
				} as ISummaryRequestParams;
				return {
					queryKey: ["cohort_eNPS_summary", params],
					queryFn: () => getNPSSummaryRange(params),
				};
			}),
		],
	});
	const [hoveredDataSetIndex, setHoveredDataSetIndex] = useState<number | null>(null);

	// create month labels for line chart (x axis labels)
	const dateRangeLabels = useMemo(() => {
		return generateDateRangeLabels(filter.value, null);
	}, [filter.value]);

	// return eNPS score data
	const allEmployeesENPSScore = useMemo(() => {
		if (eNPSSummaryData?.data.length) {
			//
			const monthlyScoreMap = eNPSSummaryData.data.reduce((acc, val) => {
				const monthBucket = format(val.time_bucket, "yyyy-MM");
				if (!acc[monthBucket]) {
					acc[monthBucket] = val.eNPS_score ? [val.eNPS_score] : [];
				} else if (val.eNPS_score) {
					acc[monthBucket].push(val.eNPS_score);
				}
				return acc;
			}, {});
			// map through all month labels and return the score
			// or null if there is no data for that month
			const monthScores: string[] = dateRangeLabels.map((month) => {
				if (monthlyScoreMap[month.value]?.length) {
					return monthlyScoreMap[month.value][0];
				}
				return null;
			});

			return monthScores;
		} else {
			return [];
		}
	}, [JSON.stringify(eNPSSummaryData?.data)]);

	/**
	 * map cohort id to score data
	 *
	 * 	{ [cohortId]: scores number[] },
	 *
	 */
	const selectedCohortsENPSScore = useMemo(() => {
		if (cohortsResponses && cohortsResponses.length && !cohortsResponses.some((result) => result.isLoading)) {
			const cohortsAverages = cohortsResponses.reduce((score, cohort) => {
				// return average of all factors for each month
				const monthlyScoreMap = cohort?.data?.data?.reduce((acc, val) => {
					const monthBucket = format(new Date(val.time_bucket), "yyyy-MM");
					if (!acc[monthBucket]) {
						acc[monthBucket] = val.eNPS_score ? [val.eNPS_score] : [];
					} else if (val.eNPS_score) {
						acc[monthBucket].push(val.eNPS_score);
					}
					return acc;
				}, {});

				const monthScores = dateRangeLabels.map((month) => {
					if (monthlyScoreMap && monthlyScoreMap[month.value]?.length) {
						return monthlyScoreMap[month.value][0];
					}
					return null;
				});

				if (cohort.data?.cohortIds) {
					if (score[cohort.data.cohortIds]) {
						score[cohort.data.cohortIds].push(monthScores);
					} else {
						score[cohort.data.cohortIds] = monthScores;
					}
				}

				return score;
			}, {});

			return cohortsAverages;
		}
	}, [JSON.stringify(cohortsResponses)]);

	// data related to populating line chart
	const legendItems: ILegendItem[] = [
		{
			id: "all",
			label: "All Employees",
			color: chooseBestContrastingColor({ background: getColor("primary", 5) as Hex, standard: 2.5 }),
			backgroundColor: getColor("primary", 5) as Hex,
		},
		...selectedCohorts.map((cohort) => ({
			id: cohort._id,
			label: cohort.name,
			color: chooseBestContrastingColor({ background: cohort.color as Hex, standard: 2.5 }),
			backgroundColor: cohort.color as Hex,
		})),
	];

	// format selected month from the store
	const indexOfSelectedMonth = useMemo(() => {
		if (month) {
			return dateRangeLabels.findIndex((label) => label.value === format(month, "yyyy-MM"));
		}
		return null;
	}, [month]);

	// format selected cohort from the store
	const indexOfSelectedCohort = useMemo(() => {
		if (cohort) {
			return legendItems.findIndex((item) => item.id === cohort);
		}
		return null;
	}, [cohort]);

	useEffect(() => {
		setHoveredDataSetIndex(indexOfSelectedCohort);
	}, [indexOfSelectedCohort]);

	const cohortsDatasets = useMemo(
		() => [
			...selectedCohorts.map((cohortModel, index) => ({
				id: cohortModel._id,
				label: cohortModel.name,
				data: selectedCohortsENPSScore?.[cohortModel._id],
				backgroundColor: cohortModel.color as Hex,
				// increased opacity unless the cohort is selected
				borderColor:
					!cohort || cohort === cohortModel._id
						? (cohortModel.color as Hex)
						: // add opacity to the color
							`${cohortModel.color as Hex}${LINE_OPACITY_HEX}`,
				pointBackgroundColor: cohortModel.color as Hex,
				borderWidth: 2,
				// prevent point from being cut off
				clip: 8,
				order: !cohort || cohort === cohortModel._id ? 0 : 1,
				pointRadius: getPointsRadiusArray(
					index + 1,
					dateRangeLabels,
					hoveredDataSetIndex,
					selectedCohortsENPSScore?.[cohortModel._id],
					indexOfSelectedMonth,
					indexOfSelectedCohort
				),
				hoverRadius: indexOfSelectedCohort === index + 1 || indexOfSelectedCohort === null ? 5 : 0,
			})),
		],
		[
			JSON.stringify(selectedCohortsENPSScore),
			hoveredDataSetIndex,
			indexOfSelectedMonth,
			indexOfSelectedCohort,
			JSON.stringify(filter.startDate),
			JSON.stringify(filter.endDate),
		]
	);

	const data = {
		labels: dateRangeLabels.map((month) => month?.label),
		datasets: [
			{
				label: "All Employees",
				data: allEmployeesENPSScore,
				backgroundColor: getColor("primary", 5),
				// increased opacity unless the "All Employees" is selected
				borderColor:
					!cohort || cohort === "all"
						? getColor("primary", 5)
						: // 50 adds 31% opacity to the color
							`${getColor("primary", 5)}50`,
				pointBackgroundColor: getColor("primary", 5),
				borderWidth: 2,
				// prevent point from being cut off
				clip: 8,
				order: !cohort || cohort === "all" ? 0 : 1,
				pointRadius: getPointsRadiusArray(
					0,
					dateRangeLabels,
					hoveredDataSetIndex,
					allEmployeesENPSScore,
					indexOfSelectedMonth,
					indexOfSelectedCohort
				),
				hoverRadius: indexOfSelectedCohort === 0 || indexOfSelectedCohort === null ? 5 : 0,
			},
			...cohortsDatasets,
		],
	};

	// Get min and max values to render the chart
	const allData = data.datasets.flatMap((obj) => obj.data);
	const max = Math.max(...allData, 0);
	const min = Math.min(...allData, 0);

	// custom tooltip
	const tooltip = {
		displayColors: false,
		backgroundColor: getColor("neutral", 7),
		footerFont: {
			weight: "normal",
			family: "Source Sans Pro",
			size: 12,
		},
		caretPadding: 8,
		cornerRadius: 4,
		filter: (tooltipItem) => {
			// don't show tooltip on other datasets if a cohort is selected
			return indexOfSelectedCohort === null || tooltipItem.datasetIndex === indexOfSelectedCohort;
		},
		callbacks: {
			title: () => {
				return "";
			},
			label: () => {
				return "";
			},
			footer: (context) => {
				if (context[0]) {
					const formattedMonthAndYear = `${context[0].label} ${
						dateRangeLabels[context[0].dataIndex].value.split("-")[0]
					}`;
					// create a separate line for each piece of data to be displayed in the tooltip
					const arrayLines = [formattedMonthAndYear, `Score: ${toFixed(context[0].formattedValue, 1)}`];

					return arrayLines;
				}
			},
		},
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		spanGaps: true,
		onHover: (event, chartElement, chart) => {
			if (indexOfSelectedCohort === null) {
				// get the index of the dataset that is being hovered over
				const dataSetIndex = chart.getElementsAtEventForMode(event, "y", {})[0]?.datasetIndex;
				if (dataSetIndex !== undefined && dataSetIndex !== hoveredDataSetIndex) {
					if (cohort) {
						setHoveredDataSetIndex(indexOfSelectedCohort ?? null);
					} else {
						setHoveredDataSetIndex(dataSetIndex);
					}
				} else if (dataSetIndex === undefined && hoveredDataSetIndex !== null) {
					setHoveredDataSetIndex(null);
				}
			}
			if (indexOfSelectedCohort === null || indexOfSelectedCohort === chartElement[0]?.datasetIndex) {
				// if hovering over a point, change cursor to pointer
				event.native.target.style.cursor = chartElement[0] ? "pointer" : "default";
			} else {
				event.native.target.style.cursor = "default";
			}
		},
		plugins: {
			tooltip,
			legend: {
				display: false,
			},
		},
		animations: {
			y: { duration: 0.2 },
		},
		scales: {
			y: {
				display: true,
				beginAtZero: true,
				ticks: {
					stepSize: max - min > 100 ? 25 : 10, // if the difference between max and min is over 100, set step size to 25 else set to 10
					padding: 8,
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
				},
				grid: {
					drawTicks: false,
					color: getColor("neutral", 2),
					lineWidth: 1,
				},
				border: {
					color: getColor("neutral", 2),
				},
			},
			x: {
				ticks: {
					padding: 8,
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
				},
				grid: {
					drawTicks: false,
					color: getColor("neutral", 2),
					lineWidth: 1,
				},
				border: {
					color: getColor("neutral", 2),
				},
			},
		},
	};

	return {
		legendItems,
		data,
		options,
		dateRangeLabels,
		isLoading,
		indexOfSelectedMonth,
		indexOfSelectedCohort,
	};
};

export default useNPSTrendlineChart;
